import { Box, Checkbox, FormControlLabel, TableCell, TableRow, Theme, Typography, useTheme } from "@mui/material";
import { CheckIcon, UnCheckIcon } from "../../../../assets/SvgIcons";
import { checkImageExists, formatCurrency, getValueColor } from "../../../../utils/utilityFunctions";
import { HoldingsTableData } from "./types";
import { useSelector } from "react-redux";
import { RootStateType } from "../../../../redux-store/reducers";
import { TableHeaderData } from "../../types";

function TableItemCheckbox({
  value,
  schemeName,
  schemeImage,
  onCheckboxClick,
  checked,
  amcId,
  folioNo
}: {
  value: string;
  schemeName: string;
  schemeImage: string;
  checked: boolean;
  onCheckboxClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
  amcId?: string;
  folioNo: string
}) {

  const amcIds = ['BDOTR', 'BPIAMC']

  const checkAmcId = amcIds.includes(amcId ?? '') ? amcId?.toLowerCase() : amcId;
  return (
    <FormControlLabel
      value={value}
      control={
        <Checkbox
          size="small"
          checkedIcon={<CheckIcon />}
          icon={<UnCheckIcon />}
          sx={{
            '&:hover': {
              backgroundColor: 'transparent',
            },
          }}
          onChange={onCheckboxClick}
          checked={checked}
        />
      }
      label={
        <Box
          sx={{
            display: "flex",
            columnGap: "12px",
            alignItems: "center"
          }}
        >
          <img
            src={checkImageExists(`${checkAmcId}`) ? `/images/amcIcons/${checkAmcId}.svg` : `/images/amcIcons/default.svg`}
            alt={schemeName}
            width={35}
            height={35}
            style={{
              objectFit: "contain",
              borderRadius: "8px"
            }}
          />
          <Typography
            variant="body_500"
            sx={(theme: Theme) => ({
              color: theme.palette.text.primary,
              '&:hover': {
                color: theme.palette.primary[500],
              },
            })}
          >
            {`${schemeName}(${folioNo})`}
          </Typography>
        </Box>
      }
      sx={{
        "& .MuiFormControlLabel-label": {
          marginLeft: "5px"
        }
      }}
    />
  );
}

export default function HoldingsTable({
  data = [],
  onCheckboxClick
}: {
  data: HoldingsTableData[];
  onCheckboxClick: (schemeId: string, checked: boolean, id: string) => void
}): JSX.Element {
  const theme = useTheme();

  const { locale, currencyCode } = useSelector((store: RootStateType) => store.currencyInfo);

  return (
    <>
      {data.map((obj: HoldingsTableData) => (
        <TableRow
          key={obj.id}
          sx={{
            '& td': {
              paddingTop: "20px",
              paddingBottom: "15px",
              borderBottom: `0.5px solid ${theme.palette.text.secondary}B2`,
              ':first-of-type': {
                paddingLeft: "0px",
              }
            },
          }}
        >
          <TableCell
            align="left"
          >
            <TableItemCheckbox
              value={obj.schemeId}
              schemeName={obj.schemeName}
              schemeImage={obj.schemeLogo}
              onCheckboxClick={(event: React.ChangeEvent<HTMLInputElement>) => onCheckboxClick(obj.schemeId, event.target.checked, obj.id)}
              checked={obj.checked}
              amcId={obj.amcId}
              folioNo={obj.folioNo ?? ''}
            />
          </TableCell>
          <TableCell align="center">
            <Typography
              variant="body_500"
              sx={(theme: Theme) => ({
                color: theme.palette.text.primary
              })}
            >
              {!Number.isNaN(Number(obj.units)) ?
                Math.round(obj.units * 100) / 100 : '-'}
            </Typography>
          </TableCell>
          <TableCell align="center">
            <Typography
              variant="body_500"
              sx={(theme: Theme) => ({
                color: theme.palette.text.primary
              })}
            >
              {obj.investedValue < 0 ? `- ${formatCurrency({ value: Math.abs(obj.investedValue), locale, currencyCode })}` : `${formatCurrency({ value: obj.investedValue, locale, currencyCode })}`}
            </Typography>
          </TableCell>
          <TableCell align="center">
            <Typography
              variant="body_500"
              sx={(theme: Theme) => ({
                color: theme.palette.text.primary
              })}
            >
              {obj.marketValue < 0 ? `- ${formatCurrency({ value: Math.abs(obj.marketValue), locale, currencyCode })}` : `${formatCurrency({ value: obj.marketValue, locale, currencyCode })}`}
            </Typography>
          </TableCell>
          <TableCell align="center">
            <Typography
              variant="body_500"
              sx={{
                color: obj.percentage.amount >= 0 ? "success.500" : "error.500",
              }}
            >
              {obj.percentage.amount < 0 ? `- ${formatCurrency({ value: Math.abs(obj.percentage.amount), locale, currencyCode })}` : `${formatCurrency({ value: obj.percentage.amount, locale, currencyCode })}`} ({!Number.isNaN(Number(obj.percentage.percentage)) ?
                Math.round(obj.percentage.percentage * 100) / 100 : ''}%)
            </Typography>
          </TableCell>
          <TableCell align="center">
            <Typography
              variant="body_500"
              sx={{
                color: obj.irr >= 0 ? "success.500" : "error.500",
              }}
            >
              {!Number.isNaN(Number(obj.irr)) ?
                Math.round(obj.irr * 100) / 100 : ''}%
            </Typography>
          </TableCell>
        </TableRow>
      ))
      }
    </>
  )
}

export function HoldingTableResponsiveBody({
  data = [],
  onCheckboxClick,
  tableHeaderData = [],
}: {
  data: HoldingsTableData[];
  onCheckboxClick: (schemeId: string, checked: boolean, id: string) => void;
  tableHeaderData?: TableHeaderData[];
}): JSX.Element {
  const theme = useTheme();
  const { locale, currencyCode } = useSelector(
    (store: RootStateType) => store.currencyInfo
  );

  const labelFor = (id: string, fallback: string): string => {
    return tableHeaderData.find((h) => h.id === id)?.label || fallback;
  };

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
        gap: "12px",
      }}
    >
      {data.map((obj: HoldingsTableData) => (
        <Box
          key={obj.id}
          sx={{
            border: `1px solid ${theme.palette.text.secondary}33`,
            borderRadius: "12px",
            padding: "12px",
            backgroundColor: theme.palette.common.white,
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
          }}
        >
          {/* Checkbox and Scheme Info */}
          <Box sx={{ display: "flex", alignItems: "flex-start" }}>
            <TableItemCheckbox
              value={obj.schemeId}
              schemeName={obj.schemeName}
              schemeImage={obj.schemeLogo}
              onCheckboxClick={(event: React.ChangeEvent<HTMLInputElement>) =>
                onCheckboxClick(obj.schemeId, event.target.checked, obj.id)
              }
              checked={obj.checked}
              amcId={obj.amcId}
              folioNo={obj.folioNo ?? ""}
            />
          </Box>

          {/* Details Two-Column Layout */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "16px",
              paddingTop: "8px",
            }}
          >
            {/* Units (Left) */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                textAlign: "left"
              }}
            >
              <Typography variant="overline_400" color="text.secondary">
                {labelFor("units", "UNITS")}
              </Typography>
              <Typography variant="body_500" color="text.primary">
                {!Number.isNaN(Number(obj.units))
                  ? Math.round(obj.units * 100) / 100
                  : "-"}
              </Typography>
            </Box>

            {/* Invested Value (Right) */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-end",
                textAlign: "right"
              }}
            >
              <Typography variant="overline_400" color="text.secondary">
                {labelFor("investedValue", "INVESTED VALUE")}
              </Typography>
              <Typography variant="body_500" color="text.primary">
                {obj.investedValue < 0
                  ? `- ${formatCurrency({
                    value: Math.abs(obj.investedValue),
                    locale,
                    currencyCode,
                  })}`
                  : `${formatCurrency({
                    value: obj.investedValue,
                    locale,
                    currencyCode,
                  })}`}
              </Typography>
            </Box>

            {/* Market Value (Left) */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                textAlign: "left"
              }}
            >
              <Typography variant="overline_400" color="text.secondary">
                {labelFor("marketValue", "MARKET VALUE")}
              </Typography>
              <Typography variant="body_500" color="text.primary">
                {obj.marketValue < 0
                  ? `- ${formatCurrency({
                    value: Math.abs(obj.marketValue),
                    locale,
                    currencyCode,
                  })}`
                  : `${formatCurrency({
                    value: obj.marketValue,
                    locale,
                    currencyCode,
                  })}`}
              </Typography>
            </Box>

            {/* Unrealised Gain/Loss (Right) */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                textAlign: "right",
                alignItems: "flex-end",
              }}
            >
              <Typography variant="overline_400" color="text.secondary">
                {labelFor("amount", "UNREALISED GAIN/LOSS (%)")}
              </Typography>
              <Typography
                variant="body_500"
                color={getValueColor(
                  "amount",
                  Number(obj.percentage.amount ?? 0),
                  theme
                )}
              >
                {obj.percentage.amount < 0
                  ? `- ${formatCurrency({
                    value: Math.abs(obj.percentage.amount),
                    locale,
                    currencyCode,
                  })}`
                  : `${formatCurrency({
                    value: obj.percentage.amount,
                    locale,
                    currencyCode,
                  })}`}{" "}
                (
                {!Number.isNaN(Number(obj.percentage.percentage))
                  ? Math.round(obj.percentage.percentage * 100) / 100
                  : ""}
                %)
              </Typography>
            </Box>

            {/* IRR (Left) */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                textAlign: "left"
              }}
            >
              <Typography variant="overline_400" color="text.secondary">
                {labelFor("irr", "Absolute Return %")}
              </Typography>
              <Typography
                variant="body_500"
                color={getValueColor("irr", Number(obj.irr ?? 0), theme)}
              >
                {!Number.isNaN(Number(obj.irr))
                  ? Math.round(obj.irr * 100) / 100
                  : ""}
                %
              </Typography>
            </Box>
          </Box>
        </Box>
      ))}
    </Box>
  );
}
